import React, { useCallback, useMemo, useState } from "react";
import Text from "@/components/ui/text";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getEntityTypeFromPageName } from "@/components/layouts/Activities/Activities";
import { useParams } from "react-router-dom";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import SearchNavigation from "@/components/controls/SerachNavigation/SearchNavigation";
import SortButton from "@/components/ui/sort-button";
import { SortingState } from "@tanstack/react-table";
import { useFilteredHistory } from "@/entities/history/hooks";
import InlineLoader from "@/components/ui/inline-loader";
import { useInfiniteScrollTrigger } from "@/hooks/useInfiniteScrollTrigger";
import LogSession from "@/components/layouts/History/LogSession";
import { LogChangeType, LogData, Method } from "@/entities/history/types";
import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { useDealCommercials } from "@/pages/deals/hooks";
import { useServiceFields } from "@/entities/services/hooks";
import { ContractData } from "@/pages/contracts/types";
import {
  SearchHighlightProvider,
  useSearchHighlight
} from "@/contexts/SearchHighlightContext";
import { find, keys, isEmpty } from "lodash";
import {
  FieldTypeLevel,
  useEntityFieldsWithAdditionals
} from "@/pages/settings/hooks";
import { APIFieldItem } from "@/pages/settings/types";

const TRANSLATION_PREFIX = "detailedView.history";
const DEFAULT_SORTING = [
  {
    id: "timeChange",
    desc: true
  }
];

// Partner contacts have no top-level list route — they live as a tab under
// /people, so the breadcrumb root points there instead of at /partners_contacts
const LIST_PATH_OVERRIDES: Partial<Record<EntityType, string>> = {
  [EntityType.PARTNER_CONTACT]: "people"
};

type BreadcrumbEntity = Pick<PartnerContactData, "firstName" | "lastName"> & {
  name?: string | null;
};

const getBreadcrumbEntityName = (
  entityType: EntityType,
  entityData: BreadcrumbEntity | undefined
): string => {
  if (!entityData) return "";
  switch (entityType) {
    case EntityType.PERSON:
      return getFullName(entityData as Partial<PersonData>);
    case EntityType.PARTNER_CONTACT:
      return getPartnerContactFullName(entityData);
    default:
      return entityData.name || "";
  }
};

const LOADING_THRESHOLD = 100;

const HistoryInner: React.FC = () => {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery } = useSearchHighlight();

  const pageName = location.pathname.split("/")[1];
  const idParam = Object.entries(params).find(([key]) =>
    key.toLowerCase().includes("id")
  );
  const entityId = idParam ? idParam[1] : undefined;
  const entityType = getEntityTypeFromPageName(pageName) ?? EntityType.DEAL;
  const useEntityQuery = getEntityHook(entityType);
  const entityQuery = useEntityQuery
    ? useEntityQuery(entityId ? [entityId] : [])
    : { entities: [], isSuccess: false };

  const { entities: [entityData] = [] } = entityQuery;

  // ALL includes additional (custom-value) fields so their changes show in
  // the log alongside standard fields
  const { data: entityFieldsResponse } = useEntityFieldsWithAdditionals(
    entityType,
    { fieldTypeLevel: FieldTypeLevel.ALL }
  );

  const dealCommercials = useDealCommercials(
    entityId || "",
    entityType === EntityType.DEAL
  );

  const isContract = entityType === EntityType.CONTRACT;
  const { data: serviceFieldsResponse } = useServiceFields({
    enabled: isContract,
    fieldTypeLevel: FieldTypeLevel.ALL
  });
  const revenueItemIds = useMemo(
    () =>
      isContract
        ? ((entityData as ContractData | undefined)?.revenueItems || []).map(
            (revenueItem) => revenueItem.id
          )
        : [],
    [isContract, entityData]
  );

  const [sorting, onSortingChange] = useState<SortingState>(DEFAULT_SORTING);

  const sortParams = useMemo(
    () =>
      sorting.length > 0
        ? {
            field: sorting[0].id,
            asc: !sorting[0].desc
          }
        : undefined,
    [sorting]
  );

  const entityFieldsMap = useMemo(
    () =>
      (entityFieldsResponse?.fields || []).reduce(
        (acc, field) => {
          acc[field.fieldKey] = field;
          return acc;
        },
        {} as Record<string, APIFieldItem>
      ),
    [entityFieldsResponse?.fields]
  );

  // Revenue item changes show inside the contract log but keep their own
  // field map — contract and revenue item schemas share field keys (status,
  // name), so merging would let one overwrite the other's config
  const serviceFieldsMap = useMemo(
    () =>
      (serviceFieldsResponse?.fields || []).reduce(
        (acc, field) => {
          acc[field.fieldKey] = field;
          return acc;
        },
        {} as Record<string, APIFieldItem>
      ),
    [serviceFieldsResponse?.fields]
  );

  const getSessionFieldsMap = useCallback(
    (session: LogData) =>
      session.changeType === LogChangeType.REVENUE_ITEMS
        ? serviceFieldsMap
        : entityFieldsMap,
    [serviceFieldsMap, entityFieldsMap]
  );

  const historyFilter = useMemo(() => {
    if (dealCommercials?.data?.id) {
      return { entityId: { in: [entityId, dealCommercials?.data?.id] } };
    }
    if (revenueItemIds.length > 0) {
      return { entityId: { in: [entityId, ...revenueItemIds] } };
    }
    return { entityId: { eq: entityId } };
  }, [entityId, dealCommercials?.data?.id, revenueItemIds]);

  const {
    entities: history,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError
  } = useFilteredHistory(
    historyFilter,
    undefined,
    sortParams ? sortParams.field : "timeChange",
    sortParams ? sortParams.asc : false
  );

  const allHistory: LogData[] = useMemo(() => {
    if (!entityData) return history;

    const creationLog = find(
      history,
      (logSession) =>
        logSession?.method === Method.INSERT &&
        logSession?.entityId === entityId
    );

    if (entityId && !creationLog) {
      const creationLog = {
        id: "created-at-log",
        entityId: entityId,
        userId: "",
        timeChange: entityData.createdAt,
        method: Method.INSERT,
        logDelta: null,
        changeType: pageName
      } as LogData;
      return sortParams?.asc
        ? [creationLog, ...history]
        : [...history, creationLog];
    }
    return history;
  }, [history, entityData, entityId, sortParams?.asc, pageName]);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, isError, fetchNextPage]);

  const { rootRef, sentinelRef } = useInfiniteScrollTrigger({
    canLoadMore: !isLoading && !isFetchingNextPage && !!hasNextPage && !isError,
    onLoadMore: handleLoadMore,
    threshold: LOADING_THRESHOLD,
    itemCount: history.length
  });

  const filteredHistory = useMemo(() => {
    if (isEmpty(entityFieldsMap)) return allHistory;

    return allHistory
      .map((session) => {
        if (!session.logDelta) return session;

        const sessionFieldsMap = getSessionFieldsMap(session);
        const filteredLogDelta = keys(session.logDelta).reduce(
          (acc, fieldName) => {
            if (
              fieldName in sessionFieldsMap &&
              fieldName !== "statusChangedAt"
            ) {
              acc[fieldName] = session.logDelta![fieldName];
            }
            return acc;
          },
          {} as typeof session.logDelta
        );

        return { ...session, logDelta: filteredLogDelta };
      })
      .filter((session) => {
        if (!session.logDelta) return true;
        return !isEmpty(session.logDelta);
      });
  }, [allHistory, entityFieldsMap, getSessionFieldsMap]);

  const allLogs = useMemo(
    () =>
      filteredHistory.map((session) => (
        <LogSession
          key={session.id}
          entityType={entityType}
          session={session}
          entityFieldsMap={getSessionFieldsMap(session)}
        />
      )),
    [filteredHistory, entityType, getSessionFieldsMap]
  );

  return (
    <div
      ref={rootRef}
      className="flex flex-col overflow-auto"
      data-test={`${entityType}-history`}
    >
      <div
        className="w-full flex flex-col gap-4 border-b border-border-border2"
        data-test={`${entityType}-history-header`}
      >
        <div
          className="flex gap-2 items-center pl-6 pr-4 min-h-12 pt-4"
          data-test={`${entityType}-history-breadcrumb`}
        >
          <Link to={`/${LIST_PATH_OVERRIDES[entityType] ?? pageName}`}>
            <Text variant="lgSubtitleH3">{t(`pages.${pageName}`)}</Text>
          </Link>
          <ChevronRight className="size-5 text-secondary" />
          <Text
            variant="lgSubtitleH3"
            className="cursor-pointer"
            onClick={() => navigate(-1)}
          >
            {getBreadcrumbEntityName(
              entityType,
              entityData as BreadcrumbEntity | undefined
            )}
          </Text>
          <ChevronRight className="size-5 text-secondary" />
          <Text variant="lgSubtitleH1">{t(`${TRANSLATION_PREFIX}.title`)}</Text>
        </div>
        <div className="flex gap-2 justify-end items-end pr-6 py-2">
          {sortParams && (
            <SortButton
              label={t("sort")}
              directionLabels={{
                asc: t(`${TRANSLATION_PREFIX}.sortDirections.asc`),
                desc: t(`${TRANSLATION_PREFIX}.sortDirections.desc`)
              }}
              onClick={() =>
                onSortingChange([
                  { id: sortParams.field, desc: sortParams.asc }
                ])
              }
              isAsc={sortParams.asc}
              className="py-1"
            />
          )}
          <SearchNavigation
            className="px-2 py-1 h-8"
            inputClassName="p-0"
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t("search")}
          />
        </div>
      </div>
      <div
        className="flex flex-col gap-8 p-6 w-full [&>*:last-child_.connecting-line]:hidden"
        data-test={`${entityType}-history-logs`}
      >
        {allLogs}

        <div ref={sentinelRef} className="w-full py-4 text-center">
          {(isLoading || isFetchingNextPage) && (
            <InlineLoader label={t("loading")} />
          )}
        </div>
      </div>
    </div>
  );
};

const History: React.FC = () => {
  return (
    <SearchHighlightProvider>
      <HistoryInner />
    </SearchHighlightProvider>
  );
};

export default History;
